import {isNil} from 'lodash';
import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {InfinityScroll} from '../../design-system/atoms/InfinityScroll/InfinityScroll';
import {Loading} from '../../design-system/atoms/Loading/Loading';
import {
  LayoutType,
  StaticLandingRankingSection,
  StaticLandingRankingSectionQueryVariables,
} from '../../graphql/generated/types';
import {onSuccessStaticLandingSection} from '../../pagewebLayout/PagewebLayoutSlice';
import {CACHE_ONLY_CONFIG} from '../../reactQuery/constant';
import {useInfiniteStaticLandingRankingSectionQuery} from '../../wrapper/landingWrapper';
import useTypedSelector from '../useTypedSelector';
import {StaticLandingRankingMoreFetcherProps} from './StaticLandingRankingMoreFetcherProps.interface';

export function StaticLandingRankingMoreFetcher({
  children,
  section,
}: StaticLandingRankingMoreFetcherProps) {
  const dispatch = useDispatch();
  const staticLandingRankingSection = useTypedSelector(
    state =>
      state.pagewebLayout.entities.sections[section.id] as Omit<
        StaticLandingRankingSection,
        'groups'
      >
  );
  const {param: currentParam} = staticLandingRankingSection;
  const param: StaticLandingRankingSectionQueryVariables['param'] = {
    type: !isNil(currentParam?.screenUid)
      ? LayoutType.Layout
      : LayoutType.Landing,
    categoryUid: currentParam?.categoryUid,
    rankingType: currentParam.rankingType?.param,
    subcategoryUid: currentParam.subcategory?.param,
    screenUid: currentParam?.screenUid,
  };
  const {data, isSuccess, fetchNextPage, isFetching, isFetchedAfterMount} =
    useInfiniteStaticLandingRankingSectionQuery(
      {
        sectionId: section.id,
        param,
      },
      {
        initialPageParam: {sectionId: section.id, param},
        getNextPageParam: lastPage => {
          const staticLandingRankingSection =
            lastPage.staticLandingRankingSection as StaticLandingRankingSection;
          return staticLandingRankingSection?.isEnd
            ? undefined
            : {
                param: {
                  ...param,
                  page: (staticLandingRankingSection?.param.page || 0) + 1,
                },
                sectionId: section.id,
              };
        },
        initialData: staticLandingRankingSection
          ? {
              // XXX: (@harry) 추후에 랜딩페이지가 PagewebLayout에서 제거되면 단일 infiniteQuery로 처리되면 좋을 거 같습니다.
              // 해당 페이지의 getInitialProps에서 LayoutQuery를 호출하여 0페이지 데이터를 가져오고 있습니다.
              // 무한스크롤시 1페이지부터 가져오기 위해 initialData를 추가합니다.
              pages: [
                {
                  staticLandingRankingSection:
                    staticLandingRankingSection as StaticLandingRankingSection,
                },
              ],
              pageParams: [{sectionId: section.id, param}],
            }
          : undefined,
        ...CACHE_ONLY_CONFIG,
      }
    );
  const lastPage = data?.pages[data.pages.length - 1]
    ?.staticLandingRankingSection as StaticLandingRankingSection | undefined;

  useEffect(() => {
    if (isFetchedAfterMount && isSuccess) {
      dispatch(
        onSuccessStaticLandingSection(lastPage, {
          sectionId: section.id,
          page: lastPage?.param.page || 0,
        })
      );
    }
  }, [dispatch, isFetchedAfterMount, isSuccess, lastPage, section.id]);

  return (
    <>
      <InfinityScroll
        isLoading={isFetching}
        canLoadNextPage={!isFetching && !lastPage?.isEnd}
        requestNextPageData={fetchNextPage}
      >
        {children}
      </InfinityScroll>
      {isFetching && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
    </>
  );
}
